import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Skeleton,
  IconButton,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  getHolidaySummary, 
  getHolidayBalance, 
  createHolidaySummary, 
  getHolidayYears 
} from '../../../api/services/Candidate/holiday';
import CustomInput from '../../FormFields/CustomInput';

const QK = {
  holidaySummary: 'holidaySummary',
  holidayBalance: 'holidayBalance',
  holidayYears: 'holidayYears',
};

const toNumber = (value) => {
  return parseFloat(value) || 0;
};

export default function HolidaySummary({ candidateId, selectedYear, onYearChange }) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [isNewYear, setIsNewYear] = useState(false);
  const [allowance, setAllowance] = useState('');
  const [carriedOver, setCarriedOver] = useState('');
  const [newYear, setNewYear] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const { data: yearsData, isLoading: yearsLoading } = useQuery(
    [QK.holidayYears, candidateId],
    () => getHolidayYears(candidateId),
    {
      enabled: !!candidateId,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    [QK.holidaySummary, candidateId, selectedYear],
    () => getHolidaySummary(candidateId, selectedYear),
    {
      enabled: !!candidateId && !!selectedYear,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: balanceData, isLoading: balanceLoading } = useQuery(
    [QK.holidayBalance, candidateId, selectedYear],
    () => getHolidayBalance(candidateId, selectedYear),
    {
      enabled: !!candidateId && !!selectedYear,
      staleTime: 2 * 60 * 1000,
    }
  );

  const createSummaryMutation = useMutation(createHolidaySummary, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.holidaySummary, candidateId]);
      queryClient.invalidateQueries([QK.holidayBalance, candidateId]);
      queryClient.invalidateQueries([QK.holidayYears, candidateId]);
      setSnackbarMessage(isNewYear ? 'New holiday year created successfully' : 'Holiday summary updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenDialog(false);
      setIsNewYear(false);
      setNewYear('');
      if (isNewYear && newYear) {
        onYearChange(newYear);
      }
    },
    onError: (error) => {
      setSnackbarMessage(error.response?.data?.message || 'Failed to update holiday summary');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const currentSummary = {
    allowance: toNumber(summaryData?.allowance),
    carried_over: toNumber(summaryData?.carried_over),
    total_available: toNumber(summaryData?.total_available),
    requested: toNumber(summaryData?.requested),
    approved: toNumber(summaryData?.approved),
    remaining: toNumber(summaryData?.remaining),
  };

  const currentBalance = {
    allowance: toNumber(balanceData?.allowance),
    carried_over: toNumber(balanceData?.carried_over),
    total_available: toNumber(balanceData?.total_available),
    requested: toNumber(balanceData?.requested),
    approved: toNumber(balanceData?.approved),
    remaining: toNumber(balanceData?.remaining),
  };

  const availableYears = yearsData?.results || yearsData || [];
  
  const years = availableYears.length > 0 
    ? availableYears.sort((a, b) => b - a) 
    : [new Date().getFullYear().toString(), (new Date().getFullYear() - 1).toString()];

  const yearExists = years.some(y => y.toString() === selectedYear);

  useEffect(() => {
    if (!yearExists && years.length > 0 && selectedYear !== years[0]?.toString()) {
      onYearChange(years[0]?.toString());
    }
  }, [years, selectedYear, yearExists, onYearChange]);

  const handleOpenDialog = (isNew = false) => {
    setIsNewYear(isNew);
    if (isNew) {
      setNewYear('');
      setAllowance('28.00');
      setCarriedOver('0.00');
    } else {
      setAllowance(currentBalance.allowance.toString());
      setCarriedOver(currentBalance.carried_over.toString());
    }
    setOpenDialog(true);
  };

  const handleSubmit = () => {
    const year = isNewYear ? parseInt(newYear) : parseInt(selectedYear);
    
    if (isNewYear && (!newYear || isNaN(parseInt(newYear)))) {
      setSnackbarMessage('Please enter a valid year');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const body = {
      candidate: candidateId,
      year: year,
      allowance: toNumber(allowance),
      carried_over: toNumber(carriedOver),
    };
    createSummaryMutation.mutate(body);
  };

  const getNextYear = () => {
    const maxYear = Math.max(...years.map(y => parseInt(y)));
    return (maxYear + 1).toString();
  };

  const isLoading = summaryLoading || balanceLoading || yearsLoading;

  const renderSkeleton = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" width={80} height={32} sx={{ borderRadius: '16px' }} />
        ))}
        <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: '16px' }} />
        <Skeleton variant="rounded" width={120} height={32} sx={{ borderRadius: '16px', ml: 'auto' }} />
      </Box>

      <Skeleton variant="text" width={250} height={24} sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Box key={i} sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: '120px' }}>
            <Card>
              <CardContent sx={{ p: 1.5 }}>
                <Skeleton variant="text" width={80} height={16} />
                <Skeleton variant="text" width={60} height={32} />
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (isLoading) {
    return renderSkeleton();
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BeachAccessIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
          Holiday Summary
        </Typography>
        <Typography variant="caption" color="#6B7280">
          Manage holiday allowance and balance
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {years.map((year) => (
              <Chip
                key={year}
                label={year}
                onClick={() => onYearChange(year.toString())}
                color={selectedYear === year.toString() ? 'primary' : 'default'}
                variant={selectedYear === year.toString() ? 'filled' : 'outlined'}
                sx={{ 
                  fontWeight: selectedYear === year.toString() ? 600 : 400,
                  minWidth: 60,
                  justifyContent: 'center'
                }}
              />
            ))}
            <Chip
              label="+ Add Year"
              onClick={() => handleOpenDialog(true)}
              color="secondary"
              variant="outlined"
              icon={<AddIcon />}
              sx={{ minWidth: 80 }}
            />
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleOpenDialog(false)}
              sx={{ ml: 'auto' }}
              disabled={!yearExists}
            >
              Edit Allowance
            </Button>
          </Box>

          {!yearExists ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No holiday data available for {selectedYear}. Please add a new year.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Holiday Year January - December {selectedYear}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: '120px' }}>
                  <Card sx={{ bgcolor: '#F8FAFC', borderRadius: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Allowance</Typography>
                      <Typography variant="h6" fontWeight="600">{currentSummary.allowance.toFixed(2)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: '120px' }}>
                  <Card sx={{ bgcolor: '#F8FAFC', borderRadius: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Carried Over</Typography>
                      <Typography variant="h6" fontWeight="600">{currentSummary.carried_over.toFixed(2)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: '120px' }}>
                  <Card sx={{ bgcolor: '#EFF6FF', borderRadius: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Available</Typography>
                      <Typography variant="h6" fontWeight="600" color="#3B82F6">{currentSummary.total_available.toFixed(2)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: '120px' }}>
                  <Card sx={{ bgcolor: '#FEF3C7', borderRadius: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Requested</Typography>
                      <Typography variant="h6" fontWeight="600" color="#F59E0B">{currentSummary.requested.toFixed(2)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: '120px' }}>
                  <Card sx={{ bgcolor: '#D1FAE5', borderRadius: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Approved</Typography>
                      <Typography variant="h6" fontWeight="600" color="#10B981">{currentSummary.approved.toFixed(2)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: '120px' }}>
                  <Card sx={{ bgcolor: '#FEE2E2', borderRadius: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Remaining</Typography>
                      <Typography variant="h6" fontWeight="600" color="#EF4444">{currentSummary.remaining.toFixed(2)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm"  
        fullWidth>
        <DialogTitle sx={{ p: 0, overflowY: "visible" }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2.5,
              pb: 1.5,
              borderBottom: '1px solid #E2E8F0',
              bgcolor: '#F8FAFC',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isNewYear ? (
                  <AddIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                ) : (
                  <EditIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                )}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} color="#1A1A2E">
                  {isNewYear ? 'Add New Holiday Year' : `Edit Holiday Allowance - ${selectedYear}`}
                </Typography>
                <Typography variant="caption" color="#6B7280">
                  {isNewYear ? 'Create a new holiday year' : 'Update holiday allowance and carried over'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpenDialog(false)}
              size="small"
              sx={{
                color: '#9CA3AF',
                '&:hover': { bgcolor: '#F3F4F6', color: '#4B5563' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5, pt: 2, overflowY: 'visible' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isNewYear && (
              <CustomInput
                label="Year"
                type="number"
                fullWidth
                value={newYear}
                onChange={(e) => setNewYear(e.target.value)}
                placeholder={getNextYear()}
                size="small"
                InputProps={{ inputProps: { min: 2000, max: 2100 } }}
              />
            )}
            <CustomInput
              label="Allowance"
              type="number"
              fullWidth
              value={allowance}
              onChange={(e) => setAllowance(e.target.value)}
              size="small"
              InputProps={{ inputProps: { min: 0, step: 0.5 } }}
            />
            <CustomInput
              label="Carried Over"
              type="number"
              fullWidth
              value={carriedOver}
              onChange={(e) => setCarriedOver(e.target.value)}
              size="small"
              InputProps={{ inputProps: { min: 0, step: 0.5 } }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              borderColor: '#D0D5DD',
              color: '#344054',
              px: 3,
              '&:hover': {
                bgcolor: '#F9FAFB',
                borderColor: '#98A2B3',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createSummaryMutation.isLoading}
            startIcon={createSummaryMutation.isLoading ? <CircularProgress size={20} /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              bgcolor: '#2469F0',
              px: 4,
              '&:hover': {
                bgcolor: '#1A5BD4',
                boxShadow: '0px 4px 12px rgba(36, 105, 240, 0.3)',
              },
            }}
          >
            {createSummaryMutation.isLoading ? 'Saving...' : isNewYear ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}